// Catálogo de bodegas. Inventario → Bodegas → Bodegas.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Pencil, Plus, Trash2, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { useTranslation } from 'react-i18next';
import { Campo, ClasificacionVenta, Location, PartnerOpt, Switch, TIPO_LOCATION, clasificacionesList, fieldCls, inv, partnersList } from './shared';

const vacio = {
  codigo: '', nombre: '', tipo: 'sucursal', es_principal: false, permite_negativo: false, unidad_venta: 'caja',
  direccion: '', telefono: '', responsable: '', observaciones: '', es_activo: true,
  latitud: '', longitud: '', supervisor_id: '', clasificacion: '', tipo_descuento: '',
  cod_estab_sri: '', pto_emision_sri: '',
};
const LETRAS = ['A', 'B', 'C', 'D', 'E', 'F'];

export function Bodegas() {
  const { t } = useTranslation('inventario');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Location[]>({
    queryKey: ['inv-locations'], queryFn: () => inv.list<Location>('locations', '?select=*&order=nombre'),
  });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: partnersList });
  const supervisores = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('supervisor')), [partners]);
  const { data: clasifs = [] } = useQuery<ClasificacionVenta[]>({ queryKey: ['inv-clasif'], queryFn: clasificacionesList });
  const supName = (id: string | null) => partners.find((p) => p.id === id)?.razon_social ?? '—';

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (l?: Location) => {
    if (l) {
      setEditId(l.id);
      setForm({
        codigo: l.codigo ?? '', nombre: l.nombre, tipo: l.tipo, es_principal: l.es_principal,
        permite_negativo: l.permite_negativo, unidad_venta: (l as Location & { unidad_venta?: string }).unidad_venta ?? 'caja',
        direccion: l.direccion ?? '', telefono: l.telefono ?? '',
        cod_estab_sri: (l as Location & { cod_estab_sri?: string }).cod_estab_sri ?? '', pto_emision_sri: (l as Location & { pto_emision_sri?: string }).pto_emision_sri ?? '',
        responsable: l.responsable ?? '', observaciones: l.observaciones ?? '', es_activo: l.es_activo,
        latitud: l.latitud != null ? String(l.latitud) : '', longitud: l.longitud != null ? String(l.longitud) : '',
        supervisor_id: l.supervisor_id ?? '', clasificacion: l.clasificacion ?? '', tipo_descuento: l.tipo_descuento ?? '',
      });
    } else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = {
        ...form, codigo: form.codigo || null, direccion: form.direccion || null, telefono: form.telefono || null,
        responsable: form.responsable || null, observaciones: form.observaciones || null,
        latitud: form.latitud === '' ? null : Number(form.latitud),
        longitud: form.longitud === '' ? null : Number(form.longitud),
        supervisor_id: form.supervisor_id || null, clasificacion: form.clasificacion || null,
        tipo_descuento: form.tipo_descuento || null,
        cod_estab_sri: form.cod_estab_sri || null, pto_emision_sri: form.pto_emision_sri || null,
      };
      return editId ? inv.update('locations', editId, body) : inv.create('locations', body);
    },
    onSuccess: () => { toast.success(t('bodegas.guardada')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['inv-locations'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.update('locations', id, { deleted_at: new Date().toISOString(), es_activo: false }),
    onSuccess: () => { toast.success(t('bodegas.desactivada')); void qc.invalidateQueries({ queryKey: ['inv-locations'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      {abierto ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}><ArrowLeft className="w-4 h-4 mr-1" /> {t('comun.volver')}</Button>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Warehouse className="w-5 h-5" /> {editId ? t('bodegas.editar') : t('bodegas.nueva_titulo')}</h1>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold flex items-center gap-2"><Warehouse className="w-5 h-5" /> {t('bodegas.titulo')}</h1>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('bodegas.nueva')}</Button>
        </div>
      )}
      {!abierto && (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('bodegas.listado', { n: rows.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('bodegas.codigo')}</th><th className="py-2 pr-3">{t('bodegas.nombre')}</th><th className="py-2 pr-3">{t('bodegas.tipo')}</th>
              <th className="py-2 pr-3">{t('bodegas.principal')}</th>
              <th className="py-2 pr-3">{t('bodegas.supervisor')}</th><th className="py-2 pr-3">{t('bodegas.clasif')}</th>
              <th className="py-2 pr-3">{t('bodegas.responsable')}</th><th className="py-2 pr-3">{t('bodegas.estado')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">{t('bodegas.sin_bodegas')}</td></tr>}
              {rows.map((l) => (
                <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono text-xs">{l.codigo ?? '—'}</td>
                  <td className="py-2 pr-3">{l.nombre}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{l.tipo}</span></td>
                  <td className="py-2 pr-3">{l.es_principal ? '✔' : ''}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{l.supervisor_id ? supName(l.supervisor_id) : '—'}</td>
                  <td className="py-2 pr-3">{l.clasificacion ? <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">{l.clasificacion}</span> : '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{l.responsable ?? '—'}</td>
                  <td className="py-2 pr-3">{l.es_activo ? t('comun.activo') : t('comun.inactivo')}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun.editar')} onClick={() => abrir(l)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title={t('bodegas.desactivar')}
                      onClick={async () => { if (await dlg.confirmar(t('bodegas.confirmar_desactivar', { nombre: l.nombre }), { peligro: true })) eliminar.mutate(l.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      )}

      {abierto && (
        <Card>
          <CardContent className="pt-4">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('bodegas.codigo')}><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
              <Campo label={t('bodegas.tipo')}>
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value })}>
                  {TIPO_LOCATION.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label={t('bodegas.unidad_venta')}>
              <select className={fieldCls} value={form.unidad_venta} onChange={(e) => set({ unidad_venta: e.target.value })}>
                <option value="caja">{t('bodegas.por_caja')}</option>
                <option value="fraccion">{t('bodegas.por_fraccion')}</option>
              </select>
              <span className="mt-1 block text-[11px] text-muted-foreground">{t('bodegas.unidad_venta_ayuda')}</span>
            </Campo>
            <Campo label={t('bodegas.nombre_req')}><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('bodegas.responsable')}><input className={fieldCls} value={form.responsable} onChange={(e) => set({ responsable: e.target.value })} /></Campo>
              <Campo label={t('bodegas.telefono')}><input className={fieldCls} value={form.telefono} onChange={(e) => set({ telefono: e.target.value })} /></Campo>
            </div>
            <Campo label={t('bodegas.direccion')}><input className={fieldCls} value={form.direccion} onChange={(e) => set({ direccion: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('bodegas.cod_estab')}><input className={fieldCls} placeholder="001" maxLength={3} value={form.cod_estab_sri} onChange={(e) => set({ cod_estab_sri: e.target.value.replace(/\D/g, '').slice(0, 3) })} /></Campo>
              <Campo label={t('bodegas.pto_emision')}><input className={fieldCls} placeholder={t('bodegas.opcional_001')} maxLength={3} value={form.pto_emision_sri} onChange={(e) => set({ pto_emision_sri: e.target.value.replace(/\D/g, '').slice(0, 3) })} /></Campo>
            </div>
            <p className="-mt-1 text-xs text-muted-foreground">{t('bodegas.cod_estab_ayuda')}</p>
            <Campo label={t('bodegas.observaciones')}><textarea className={`${fieldCls} h-16`} value={form.observaciones} onChange={(e) => set({ observaciones: e.target.value })} /></Campo>

            <p className="text-xs font-semibold uppercase text-muted-foreground border-t pt-3">{t('bodegas.geo_clasif')}</p>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('bodegas.latitud')}><input type="number" step="0.0000001" className={fieldCls} placeholder="-0.1807" value={form.latitud} onChange={(e) => set({ latitud: e.target.value })} /></Campo>
              <Campo label={t('bodegas.longitud')}><input type="number" step="0.0000001" className={fieldCls} placeholder="-78.4678" value={form.longitud} onChange={(e) => set({ longitud: e.target.value })} /></Campo>
              <Campo label={t('bodegas.supervisor')}>
                <select className={fieldCls} value={form.supervisor_id} onChange={(e) => set({ supervisor_id: e.target.value })}>
                  <option value="">{t('bodegas.sin_supervisor')}</option>
                  {supervisores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                </select>
              </Campo>
              <Campo label={t('bodegas.clasif_ventas')}>
                <select className={fieldCls} value={form.clasificacion} onChange={(e) => set({ clasificacion: e.target.value })}>
                  <option value="">{t('bodegas.automatica')}</option>
                  {clasifs.map((c) => <option key={c.id} value={c.letra}>{c.letra}{c.descripcion ? ` · ${c.descripcion}` : ''}</option>)}
                </select>
              </Campo>
              <Campo label={t('bodegas.tipo_desc')}>
                <select className={fieldCls} value={form.tipo_descuento} onChange={(e) => set({ tipo_descuento: e.target.value })}>
                  <option value="">{t('bodegas.sin_descuento')}</option>
                  {LETRAS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </Campo>
            </div>
            {form.latitud && form.longitud && (
              <a className="text-xs text-primary hover:underline" target="_blank" rel="noopener noreferrer"
                href={`https://www.google.com/maps?q=${form.latitud},${form.longitud}`}>{t('bodegas.ver_mapa')}</a>
            )}

            <div className="grid grid-cols-2 gap-2 border-t pt-3">
              <Switch checked={form.es_principal} onChange={(v) => set({ es_principal: v })} label={t('bodegas.es_principal')} />
              <Switch checked={form.permite_negativo} onChange={(v) => set({ permite_negativo: v })} label={t('bodegas.permite_negativo')} />
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label={t('bodegas.activa')} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun.cancelar')}</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>{t('comun.guardar')}</Button>
              </div>
            </div>
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
